import { Controller, useForm } from 'react-hook-form'

import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { SelectDropdown } from '@/shared/components/SelectDropdown/SelectDropdown'
import { TextField } from '@/shared/components/TextField/TextField'
import { pxToRem } from '@/shared/styles/themes'
import { Alert, Box, Divider, Grid2 as Grid, styled, Typography } from '@mui/material'

import { DepotFormValues, GeneralPreferencesFormValues, GroupFormValues, RunFormValues } from '../models/OnboardingFormFields'

export type RunSetupFormProps = {
  initialValues: RunFormValues
  groupData: GroupFormValues
  generalPreferencesData: GeneralPreferencesFormValues
  depotData: DepotFormValues
  onSubmit: (data: RunFormValues) => void
  isSubmitting: boolean
}

export const RunSetupForm: React.FC<RunSetupFormProps> = ({
  initialValues,
  groupData,
  generalPreferencesData,
  depotData,
  onSubmit,
  isSubmitting,
}) => {
  const { control, handleSubmit } = useForm<RunFormValues>({
    defaultValues: {
      ...initialValues,
      groupId: groupData.id,
      startDepotId: depotData.id,
      finishDepotId: depotData.id,
    },
  })

  const { data: colorOptions, completed: fetchColorsCompleted } = useDropdown('htmlColors')

  const onFormSubmit = (data: RunFormValues) => {
    // Ensure we're only sending ID references
    data.groupId = groupData.id
    data.startDepotId = depotData.id
    data.finishDepotId = depotData.id

    onSubmit(data)
  }

  return (
    <StyledForm id="onboarding-step-3" onSubmit={handleSubmit(onFormSubmit)}>
      <Typography variant="h2" sx={{ fontSize: pxToRem(24), fontWeight: 500, mb: 2 }}>
        Set Up Your First Run
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Runs define your delivery or pickup routes. Configure your first run to start planning operations.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Runs represent the vehicles in your fleet that will perform deliveries and pickups.
      </Alert>

      {/* General Information Section */}
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
        General Information
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Run name is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Run Name"
                placeholder="Enter run name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="uiColorId"
            control={control}
            rules={{ required: 'Color is required' }}
            render={({ field, fieldState }) => (
              <SelectDropdown
                {...field}
                label="Run UI Color"
                disableClearable={true}
                placeholder="Select color"
                options={colorOptions}
                onChange={(_, value) => field.onChange(value)}
                value={field.value}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                getOptionLabel={(option) => option?.label || ''}
                disabled={!fetchColorsCompleted || isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                renderOption={(props, option) => {
                  const { ...optionProps } = props

                  return (
                    <Box
                      component="li"
                      {...optionProps}
                    >
                      <Box
                        sx={{
                          width: pxToRem(12),
                          height: pxToRem(12),
                          borderRadius: pxToRem(2),
                          marginRight: pxToRem(8),
                          backgroundColor: (option as DropdownOption).value,
                        }}
                      />
                      {(option as DropdownOption).label}
                    </Box>
                  )
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Description"
                placeholder="Enter run description"
                multiline
                rows={3}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Depot Assignment Section */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
        Group & Depot Assignment
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="groupId"
            control={control}
            render={() => (
              <TextField
                label="Group"
                value={groupData.name}
                disabled={true}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="startDepotId"
            control={control}
            rules={{ required: 'Start depot is required' }}
            render={() => (
              <TextField
                label="Start Depot"
                value={depotData.name}
                disabled={true}
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name="finishDepotId"
            control={control}
            rules={{ required: 'Finish depot is required' }}
            render={() => (
              <TextField
                label="Finish Depot"
                value={depotData.name}
                disabled={true}
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Capacity Section */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
        Vehicle Capacity
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="weightCapacity"
            control={control}
            rules={{
              required: 'Weight capacity is required',
              min: { value: 0, message: 'Weight capacity must be positive' },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="number"
                label={`Weight Capacity (${generalPreferencesData.weightUnit})`}
                placeholder="Enter weight capacity"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                inputProps={{ min: 0 }}
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="volumeCapacity"
            control={control}
            rules={{
              required: 'Volume capacity is required',
              min: { value: 0, message: 'Volume capacity must be positive' },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="number"
                label={`Volume Capacity (${generalPreferencesData.volumeUnit})`}
                placeholder="Enter volume capacity"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                inputProps={{ min: 0 }}
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>
      </Grid>
    </StyledForm>
  )
}

const StyledForm = styled('form')`
  width: 100%;
`